import { reject } from 'ramda'
import ApiService from 'api-client/ApiService'
import type { VolumeDriftResult } from 'app/plugins/infrastructure/components/common/service-health/storage-connectivity/model'
import type {
  ClusterHostGroup,
  CreateClusterHostGroup,
} from 'app/plugins/openstack/components/infrastructure/clusters/model'
import type {
  AdvancedOverridesPatch,
  DryRunResult,
  OverrideCatalogsByRole,
  OverridesState,
} from 'app/plugins/openstack/components/infrastructure/hosts/advanced-overrides/models'
import { parseNdjson } from 'core/audit-events/helpers'
import type { AuditEvent, AuditEventsQuery } from 'core/audit-events/models'
import type { HostNetworkConfiguration } from 'openstack/components/infrastructure/blueprint/model'
import { isNilOrEmpty } from 'utils/fp'
import { trackApiMethodMetadata } from './helpers'
import type {
  Host,
  HostLogFetchRequest,
  HostLogFetchResult,
  HostLogFetchStarted,
} from './resmgr.model'

// Overrides writes guard against concurrent edits with an optional expected_version
const withExpectedVersion = (url: string, expectedVersion?: number) =>
  expectedVersion !== undefined ? `${url}?expected_version=${expectedVersion}` : url

class ResMgr extends ApiService {
  hasEndpoint

  public getClassName() {
    return 'resmgr'
  }

  static apiMethodsMetadata = []

  protected async getEndpoint() {
    const endpoint = await this.client.keystone.getServiceEndpoint('resmgr', 'public')
    this.hasEndpoint = endpoint ? true : false
    return `${endpoint}/v1`
  }

  endpointPresent() {
    return this.hasEndpoint
  }

  @trackApiMethodMetadata({ url: '/hosts', type: 'GET' })
  async getHosts() {
    const url = `/hosts`
    return this.client.basicGet<Host[]>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getHosts',
      },
    })
  }

  // TODO: mthdName below duplicates getHosts' — likely copy-paste, affects telemetry naming only
  @trackApiMethodMetadata({ url: '/hosts', type: 'GET' })
  async getResmgrHosts() {
    return this.client.basicGet<Host[]>({
      url: '/hosts',
      options: {
        clsName: this.getClassName(),
        mthdName: 'getHosts',
      },
    })
  }

  @trackApiMethodMetadata({ url: '/hosts/:id', type: 'GET' })
  async getResmgrHostV1(id) {
    return this.client.basicGet<Host>({
      url: `/hosts/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getResmgrHostV1',
      },
    })
  }

  @trackApiMethodMetadata({ url: '/hosts/:id', type: 'GET' })
  async getResmgrHostV2(id) {
    return this.client.basicGet<Host>({
      url: `/hosts/${id}`,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'getResmgrHostV2',
      },
    })
  }

  // LUNMAP-FR-002: what Cinder, Nova, the array and libvirt on the host record about one
  // hypervisor's volumes (Cinder's volume-drift plus libvirt, PCD-8796). `backend` (host@backend)
  // keeps only that backend's volumes (LUNMAP-FR-010), `configuration` (a cinder.conf section)
  // only that configuration's (LUNMAP-FR-011). resmgr reuses a result for 30 seconds unless
  // `refresh` is set, and answers 429 with Retry-After while too many checks run.
  async getStorageDrift(
    hostId: string,
    scope: { backend?: string; configuration?: string; refresh?: boolean } = {},
  ): Promise<VolumeDriftResult> {
    const { backend, configuration, refresh } = scope
    return this.client.basicGet<VolumeDriftResult>({
      url: `/hosts/${encodeURIComponent(hostId)}/storage-drift`,
      version: 'v2',
      params: {
        ...(configuration ? { configuration } : backend ? { backend } : {}),
        ...(refresh ? { refresh: 'true' } : {}),
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'getStorageDrift',
      },
    })
  }

  @trackApiMethodMetadata({ url: '/hosts/:hostId/logs', type: 'POST', params: ['hostId'] })
  async startHostLogFetch(hostId: string, body: HostLogFetchRequest) {
    return this.client.basicPost<HostLogFetchStarted>({
      url: `/hosts/${hostId}/logs`,
      version: 'v2',
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'startHostLogFetch',
      },
    })
  }

  @trackApiMethodMetadata({
    url: '/hosts/:hostId/logs/:fetchId',
    type: 'GET',
    params: ['hostId', 'fetchId'],
  })
  async getHostLogFetch(hostId: string, fetchId: string) {
    return this.client.basicGet<HostLogFetchResult>({
      url: `/hosts/${hostId}/logs/${fetchId}`,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'getHostLogFetch',
      },
    })
  }

  @trackApiMethodMetadata({
    url: '/hosts/:hostId/roles/:role',
    type: 'PUT',
    params: ['hostId', 'role'],
  })
  async addRole(hostId, role, body) {
    const url = `/hosts/${hostId}/roles/${role}`
    return this.client.basicPut({
      url,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'addRole',
      },
    })
  }

  @trackApiMethodMetadata({
    url: '/hosts/:hostId/roles/:role',
    type: 'DELETE',
    params: ['hostId', 'role'],
  })
  async removeRole(hostId, role): Promise<void> {
    const url = `/hosts/${hostId}/roles/${role}`
    await this.client.basicDelete({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'removeRole',
      },
    })
  }

  @trackApiMethodMetadata({
    url: '/hosts/:hostId/roles/:role',
    type: 'GET',
    params: ['hostId', 'role'],
  })
  async getRole<T>(hostId, role) {
    const url = `/hosts/${hostId}/roles/${role}`
    return this.client.basicGet<T>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getRole',
      },
    })
  }

  @trackApiMethodMetadata({
    url: '/hosts/:hostId',
    type: 'DELETE',
    params: ['hostId'],
  })
  async unauthorizeHost(id) {
    const url = `/hosts/${id}`
    return this.client.basicDelete({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'unauthorizeHost',
      },
    })
  }

  @trackApiMethodMetadata({ url: '/services/:service', type: 'GET', params: ['service'] })
  async getService(service) {
    const url = `/services/${service}`
    return this.client.basicGet({
      url,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'getService',
      },
    })
  }

  @trackApiMethodMetadata({
    url: '/services/:service',
    type: 'PUT',
    params: ['service'],
  })
  async updateService(service, body) {
    const url = `/services/${service}`
    return this.client.basicPut({
      url,
      version: 'v2',
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateService',
      },
    })
  }

  async getBlueprints() {
    const url = `/blueprint`
    return this.client.basicGet({
      url,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'getBlueprints',
      },
    })
  }

  async getBlueprint({ name }) {
    const url = `/blueprint/${name}`
    return this.client.basicGet({
      url,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'getBlueprint',
      },
    })
  }

  async createBlueprint({ body }) {
    const url = `/blueprint`
    return this.client.basicPost({
      url,
      version: 'v2',
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createBlueprint',
      },
    })
  }

  async updateBlueprint({ name, body }) {
    const url = `/blueprint/${name}`
    return this.client.basicPut({
      url,
      version: 'v2',
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateBlueprint',
      },
    })
  }

  async getHostNetworkConfigs() {
    const url = `/hostconfigs`
    return this.client.basicGet<HostNetworkConfiguration[]>({
      url,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'getHostNetworkConfigs',
      },
    })
  }

  async createHostNetworkConfigs(body) {
    const url = `/hostconfigs`
    return this.client.basicPost({
      url,
      version: 'v2',
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createHostNetworkConfigs',
      },
    })
  }

  async updateHostNetworkConfig({ id, body }) {
    const url = `/hostconfigs/${id}`
    return this.client.basicPut({
      url,
      version: 'v2',
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateHostNetworkConfig',
      },
    })
  }

  async deleteHostNetworkConfig(id) {
    const url = `/hostconfigs/${id}`
    await this.client.basicDelete({
      url,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteHostNetworkConfig',
      },
    })
  }

  async assignHypervisorRole({ id, hostcluster = undefined }) {
    const url = `/hosts/${id}/roles/hypervisor`
    return this.client.basicPut({
      url,
      version: 'v2',
      body: { ...(hostcluster && { hostcluster }) },
      options: {
        clsName: this.getClassName(),
        mthdName: 'assignHypervisorRole',
      },
    })
  }

  async assignImageLibraryRole({ id }) {
    const url = `/hosts/${id}/roles/image-library`
    return this.client.basicPut({
      url,
      version: 'v2',
      body: {},
      options: {
        clsName: this.getClassName(),
        mthdName: 'assignImageLibraryRole',
      },
    })
  }

  async assignPersistentStorageRole({ id, body }) {
    const url = `/hosts/${id}/roles/persistent-storage`
    return this.client.basicPut({
      url,
      version: 'v2',
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'assignImageLibraryRole',
      },
    })
  }

  async deauthorizeHypervisorRole({ id }) {
    const url = `/hosts/${id}/roles/hypervisor`
    await this.client.basicDelete({
      url,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'removeHypervisorRole',
      },
    })
  }

  async deauthorizeImageLibraryRole({ id }) {
    const url = `/hosts/${id}/roles/image-library`
    await this.client.basicDelete({
      url,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'removeImageLibraryRole',
      },
    })
  }

  async deauthorizePersistentStorageRole({ id }) {
    const url = `/hosts/${id}/roles/persistent-storage`
    await this.client.basicDelete({
      url,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'removePersistentStorageRole',
      },
    })
  }

  async getHostsv2() {
    const url = `/hosts`
    return this.client.basicGet<any[]>({
      url,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'getHostsv2',
      },
    })
  }

  async assignHostConfig({ hostId, configId }) {
    const url = `/hosts/${hostId}/hostconfig/${configId}`
    return this.client.basicPut({
      url,
      version: 'v2',
      body: {},
      options: {
        clsName: this.getClassName(),
        mthdName: 'assignHostConfiguration',
      },
    })
  }

  async unassignHostConfig({ hostId, configId }) {
    const url = `/hosts/${hostId}/hostconfig/${configId}`
    return this.client.basicDelete({
      url,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'unassignHostConfiguration',
      },
    })
  }

  async assignDns({ hostId }: { hostId: string }) {
    const url = `/hosts/${hostId}/roles/dns`
    return this.client.basicPut({
      url,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'assignDns',
      },
    })
  }

  async unassignDns(hostId: string) {
    const url = `/hosts/${hostId}/roles/dns`
    return this.client.basicDelete({
      url,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'unassignDns',
      },
    })
  }

  async createClusterHostGroup({ body }: { body: CreateClusterHostGroup }) {
    const url = `/clusters`
    return this.client.basicPost<ClusterHostGroup>({
      url,
      version: 'v2',
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createClusterHostGroup',
      },
    })
  }

  async deleteClusterHostGroup({ name }) {
    const url = `/clusters/${name}`
    return this.client.basicDelete({
      url,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteClusterHostGroup',
      },
    })
  }

  async updateClusterHostGroup({ name, body }: { name: string; body: CreateClusterHostGroup }) {
    const url = `/clusters/${name}`
    return this.client.basicPut<ClusterHostGroup>({
      url,
      version: 'v2',
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateClusterHostGroup',
      },
    })
  }

  async getClusterHostGroups() {
    const url = `/clusters`
    const res = await this.client.basicGet<ClusterHostGroup[]>({
      url,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'getClusterHostGroups',
      },
    })
    return res
  }

  async getClusterHostGroup({ name }) {
    const url = `/clusters/${name}`
    return this.client.basicGet({
      url,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'getClusterHostGroup',
      },
    })
  }
  async downloadAuditLogs({ urlParams }) {
    const urlParamsString = urlParams.map((param) => `${param.key}=${param.value}`).join('&')
    return this.client.basicGet({
      url: `/audit/api/v1/logs/export?${urlParamsString}`,
      endpoint: '',
      options: {
        clsName: this.getClassName(),
        mthdName: 'downloadAuditLogs',
        extractNestedData: false,
        config: {
          responseType: 'blob',
        },
      },
    })
  }

  // NCP-FR-002: Audit events for the Notification Center. The export endpoint streams NDJSON,
  // so it is read as text and parsed line by line; `service` is only sent for a Component filter
  async getAuditEvents(query: AuditEventsQuery): Promise<AuditEvent[]> {
    const { startTime, endTime, limit, region, component, action, result } = query
    const params = reject(isNilOrEmpty, {
      start_time: startTime,
      end_time: endTime,
      limit,
      region,
      service: component,
      action,
      outcome: result,
    })
    const text = await this.client.basicGet<string>({
      url: '/audit/api/v1/logs/export',
      endpoint: '',
      params,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getAuditEvents',
        extractNestedData: false,
        config: {
          responseType: 'text',
        },
      },
    })
    // An empty body comes back from normalizeResponse as [] rather than ''
    return parseNdjson<AuditEvent>(typeof text === 'string' ? text : '')
  }

  async getAuditLogFilters() {
    return this.client.basicGet<any>({
      url: '/audit/api/v1/logs/filters',
      endpoint: '',
      options: {
        clsName: this.getClassName(),
        mthdName: 'getAuditLogFilters',
      },
    })
  }

  async getOverrideCatalog(role?: string) {
    const url = `/roles/override_catalog`
    return this.client.basicGet<OverrideCatalogsByRole>({
      url,
      version: 'v2',
      params: role ? { role } : undefined,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOverrideCatalog',
      },
    })
  }

  async getHostRoleOverrides(hostId: string, role: string) {
    const url = `/hosts/${hostId}/roles/${role}/overrides`
    return this.client.basicGet<OverridesState>({
      url,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'getHostRoleOverrides',
      },
    })
  }

  async replaceHostRoleOverrides(
    hostId: string,
    role: string,
    body: { advanced_overrides: AdvancedOverridesPatch; expected_version?: number },
    { dryRun = false }: { dryRun?: boolean } = {},
  ) {
    const url = `/hosts/${hostId}/roles/${role}/overrides${dryRun ? '?dry_run=true' : ''}`
    return this.client.basicPut<OverridesState | DryRunResult>({
      url,
      version: 'v2',
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'replaceHostRoleOverrides',
      },
    })
  }

  async revertHostRoleOverrides(
    hostId: string,
    role: string,
    { expectedVersion }: { expectedVersion?: number } = {},
  ) {
    const url = withExpectedVersion(
      `/hosts/${hostId}/roles/${role}/overrides/revert`,
      expectedVersion,
    )
    return this.client.basicPost<OverridesState>({
      url,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'revertHostRoleOverrides',
      },
    })
  }

  async deleteHostRoleOverrides(
    hostId: string,
    role: string,
    { expectedVersion }: { expectedVersion?: number } = {},
  ) {
    const url = withExpectedVersion(`/hosts/${hostId}/roles/${role}/overrides`, expectedVersion)
    return this.client.basicDelete<OverridesState>({
      url,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteHostRoleOverrides',
      },
    })
  }

  async deleteHostFileOverrides(
    hostId: string,
    role: string,
    fileId: string,
    { expectedVersion }: { expectedVersion?: number } = {},
  ) {
    const url = withExpectedVersion(
      `/hosts/${hostId}/roles/${role}/overrides/${encodeURIComponent(fileId)}`,
      expectedVersion,
    )
    return this.client.basicDelete<OverridesState>({
      url,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteHostFileOverrides',
      },
    })
  }

  async reapplyHostRoleOverrides(hostId: string, role: string) {
    return this.client.basicPost<OverridesState>({
      url: `/hosts/${hostId}/roles/${role}/overrides/reapply`,
      version: 'v2',
      options: {
        clsName: this.getClassName(),
        mthdName: 'reapplyHostRoleOverrides',
      },
    })
  }
}

export default ResMgr
